import { useState } from "react";
import { Button as AntButton, Select as AntSelect, Space } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import PropTypes from "prop-types";
import { BiPlus, BiTrash } from "react-icons/bi";
import { useLazyQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { ACTION_TYPES } from "utils/constants";
import ConditionListEditor from "./ConditionListEditor";

const SYNC_TYPES = [
  ACTION_TYPES.SYNC_PROCESSES,
  ACTION_TYPES.NO_SYNC_PROCESSES,
];

const isSync = (action) => SYNC_TYPES.includes(get(action, "type"));

const isPin = (action) =>
  get(action, "type") === ACTION_TYPES.NO_SYNC_PROCESSES;

/**
 * Keeps the web and the advisor journeys aligned, the way classic funnels do.
 * A row either mirrors a step onto another process, or pins that process where
 * it stands. Stored as `SYNC_PROCESSES` / `NO_SYNC_PROCESSES` actions so the
 * canvas and the legacy process editor read the same data.
 */
const MirrorStatusListField = ({
  actions,
  processId,
  variables,
  conditionLabels,
  onChange,
  labels,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [processes, setProcesses] = useState([]);

  const [loadProcesses, { loading }] = useLazyQuery(graphql.queries.PROCESSES, {
    onCompleted: (result) => {
      setProcesses(get(result, "processes.data") || []);
      setLoaded(true);
    },
  });

  const ensureProcesses = () => {
    if (loaded || loading) return;
    loadProcesses({ variables: { take: 500 } });
  };

  const mirrors = (actions || []).filter(isSync);
  const others = (actions || []).filter((action) => !isSync(action));

  const emit = (nextMirrors) => onChange([...others, ...nextMirrors]);

  const replaceAt = (index, patch) =>
    emit(
      mirrors.map((mirror, position) =>
        position === index ? { ...mirror, ...patch } : mirror,
      ),
    );

  const add = (type) => {
    ensureProcesses();
    emit([...mirrors, { id: cuid(), type, args: {}, conditions: [] }]);
  };

  const remove = (index) =>
    emit(mirrors.filter((_, position) => position !== index));

  const processOptions = processes
    .filter(({ id }) => id !== processId)
    .map(({ id, name }) => ({ value: id, label: name || id }));

  const statusOptions = (targetProcessId) =>
    get(
      processes.find(({ id }) => id === targetProcessId),
      "processStatuses",
      [],
    )
      .slice()
      .sort((a, b) => parseInt(a.order, 10) - parseInt(b.order, 10))
      .map(({ id, name }) => ({ value: id, label: name || id }));

  return (
    <Space direction="vertical" size={8} className="canvas-editor__full-width">
      <div className="canvas-editor__meta">{labels.MIRROR_HINT}</div>
      {mirrors.map((mirror, index) => {
        const targetProcessId = get(mirror, "args.process");
        const pinned = isPin(mirror);
        return (
          <Space
            direction="vertical"
            size={4}
            className="canvas-editor__full-width"
            key={mirror.id || index}
          >
            <div className="canvas-editor__row">
              <AntSelect
                showSearch
                virtual={false}
                className="canvas-editor__full-width"
                placeholder={labels.MIRROR_PROCESS}
                optionFilterProp="label"
                loading={loading}
                options={processOptions}
                value={targetProcessId || undefined}
                onDropdownVisibleChange={ensureProcesses}
                onChange={(value) =>
                  replaceAt(index, { args: { process: value } })
                }
              />
              <AntSelect
                showSearch
                virtual={false}
                className="canvas-editor__full-width"
                placeholder={
                  pinned ? labels.MIRROR_PIN_STATUS : labels.MIRROR_STATUS
                }
                optionFilterProp="label"
                disabled={pinned || !targetProcessId}
                options={statusOptions(targetProcessId)}
                value={get(mirror, "args.status") || undefined}
                onDropdownVisibleChange={ensureProcesses}
                onChange={(value) =>
                  replaceAt(index, {
                    args: { process: targetProcessId, status: value },
                  })
                }
              />
              <AntButton
                danger
                icon={<BiTrash />}
                onClick={() => remove(index)}
              />
            </div>
            <div className="canvas-editor__meta">
              {pinned ? labels.MIRROR_PIN_HINT : labels.MIRROR_SYNC_HINT}
            </div>
            <ConditionListEditor
              conditions={mirror.conditions || []}
              variables={variables}
              labels={conditionLabels}
              onChange={(conditions) => replaceAt(index, { conditions })}
            />
          </Space>
        );
      })}
      <div className="canvas-editor__row">
        <AntButton
          icon={<BiPlus />}
          onClick={() => add(ACTION_TYPES.SYNC_PROCESSES)}
        >
          {labels.MIRROR_ADD}
        </AntButton>
        <AntButton
          icon={<BiPlus />}
          onClick={() => add(ACTION_TYPES.NO_SYNC_PROCESSES)}
        >
          {labels.MIRROR_PIN_ADD}
        </AntButton>
      </div>
    </Space>
  );
};

MirrorStatusListField.propTypes = {
  actions: PropTypes.array,
  processId: PropTypes.string,
  variables: PropTypes.array,
  conditionLabels: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  labels: PropTypes.object,
};

MirrorStatusListField.defaultProps = {
  actions: [],
  processId: null,
  variables: [],
  conditionLabels: {},
  labels: {},
};

export default MirrorStatusListField;
